import Button from "@/components/button/Button"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { useBreakpoints } from "@/components/screen"
import BookDemoButton from "@/components/website/book-demo-button/BookDemoButton"
import GetFreeQuoteButton from "@/components/website/get-quote-button/GetFreeQuoteButton"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { websiteRoutes } from "@/router/routes"
import { Row } from "antd"
import { FC } from "react"
import { useNavigate } from "react-router"
import bgImage from "./images/action-plate-bg.png"
import simplifyBgImage from "./images/banner-bg-img.png"
import bgImageMob from "./images/banner-mobile-logo.png"
import eLogoImg from "./images/e-logo.png"

import { SvgIcon } from "@/components/icon"
import "./ClaimBanner.less"

const { Title, Paragraph, Text } = WebsiteTypography

export type ClaimBannerType = "book-demo" | "get-quote" | "get-started" | "partners-channel" | "ready-to-simplify"
export type ClaimBannerProps = {
  type: ClaimBannerType
}

type TickedListItemProps = {
  text: string
}

const tickedList: TickedListItemProps[] = [
  { text: "No duplication" },
  { text: "No cost" },
  { text: "Just one application" },
]

const TickedListItem: FC<TickedListItemProps> = ({ text }) => (
  <div className={"ticked-list-item"}>
    <div className={"white-hexagon-container"}>
      <SvgIcon type={"small-white-tick"} color={"orange"} />
    </div>
    <Text weight={"w700"} color={"black"} style={{ marginLeft: 8 }}>
      {text}
    </Text>
  </div>
)

const ClaimBanner: FC<ClaimBannerProps> = ({ type }) => {
  const { isXxs, isXs, isSm } = useBreakpoints()
  const { openDarkModal } = useModalDarkContext()

  const navigate = useNavigate()
  const { website } = websiteRoutes
  const onGetStarted = () => navigate(`/easetech${website.quote}`)
  const handleGetInTouch = () => {
    openDarkModal({
      type: "quick-contact",
      width: "width900",
      isLogo: true,
    })
  }

  return (
    <>
      {type === "ready-to-simplify" && (
        <div className={"banner-wrapper simplify-version"}>
          <div className={"simplify-bg-image-wrapper"}>
            <img src={simplifyBgImage} alt={"logo picture"} />
          </div>
          <div className={"simplify-text-block"}>
            <Title level={4} color={"white"} lh={"lh-sm"} centered style={{ paddingBottom: 8 }}>
              <span className={"bold"}>{"Ready to simplify "}</span>
              <span className={"w300"}>{"your wayleave process?"}</span>
            </Title>
          </div>

          <Paragraph color={"beige"} centered>
            {
              "See how Easetech can help your team process more applications, reduce costs,\nand improve turnaround times."
            }
          </Paragraph>

          <div className={"simplify-btns-block"}>
            <BookDemoButton type={"darkBlue"} />
            <Button
              btnType={"ghost"}
              textColor={"white"}
              size={"large"}
              text={"GET IN TOUCH"}
              onClick={handleGetInTouch}
              bordered={true}
            />
          </div>

          <div className={"closing-text-wrapper"}>
            <Paragraph color={"beige"} size={"xs"} centered>
              {
                "Trusted by organisations looking to automate workflows, scale operations, and improve profitability through smarter wayleave management."
              }
            </Paragraph>
          </div>
        </div>
      )}
      {type === "book-demo" && (
        <div className={"banner-wrapper"}>
          <div className={"banner-bg-image-wrapper"}>
            <img src={isXxs || isXs || isSm ? bgImageMob : bgImage} alt={"logo picture"} />
          </div>
          <div className={"banner-text-block"}>
            <Title level={4} color={"white"} lh={"lh-sm"} weight={"w700"} className={"banner-title"}>
              {"Find out "}
              <span>{"how our solutions"}</span>
              <span style={{ display: "block" }}>{"can benefit you..."}</span>
            </Title>
          </div>
          <BookDemoButton type={"darkBlue"} />
        </div>
      )}
      {type === "get-quote" && (
        <div className={"banner-wrapper"}>
          <div className={"banner-bg-image-wrapper"}>
            <img src={isXxs || isXs || isSm ? bgImageMob : bgImage} alt={"logo picture"} />
          </div>
          <div className={"banner-text-block"}>
            <Title level={4} color={"white"} lh={"lh-sm"} weight={"w700"} className={"banner-title"}>
              {"Request a Quote "}
              <span>{"Today"}</span>
            </Title>
            <Paragraph style={{ paddingTop: 10 }}>
              {"Let us handle your wayleave approvals while you focus on your core business."}
            </Paragraph>
          </div>
          <GetFreeQuoteButton type={"darkBlue"} />
        </div>
      )}
      {type === "get-started" && (
        <div className={"banner-wrapper"}>
          <div className={"banner-bg-image-wrapper"}>
            <img src={isXxs || isXs || isSm ? bgImageMob : bgImage} alt={"logo picture"} />
          </div>
          <div className={"banner-text-block"}>
            <Title level={4} color={"white"} lh={"lh-sm"} weight={"w700"} className={"banner-title"}>
              {"Request a Quote "}
              <span>{"Today"}</span>
            </Title>
            <Paragraph style={{ paddingTop: 10 }}>
              {"Let us handle your wayleave approvals while you focus on your core business."}
            </Paragraph>
          </div>
          <Row justify={"center"}>
            <Button text={"GET A QUOTE"} onClick={onGetStarted} btnType={"dark-blue"} />
          </Row>
        </div>
      )}
      {type === "partners-channel" && (
        <div className={"banner-wrapper partners"}>
          <div className={"banner-bg-image-wrapper"}>
            <img src={eLogoImg} alt={"logo picture"} />
          </div>
          <div className={"banner-content-block"}>
            <Title level={4} centered color={"white"} weight={"w700"} className={"banner-title"}>
              {"Simplify Your Wayleave\nSubmission"}
            </Title>

            <div className={"ticked-list-container"}>
              {tickedList && tickedList.map((it, index) => <TickedListItem key={index} text={it.text} />)}
            </div>

            <Text weight={"w700"}>{"Delivered to all utilities in our network"}</Text>
          </div>
        </div>
      )}
      )
    </>
  )
}

export default ClaimBanner
